import React, { useState, useEffect, useRef } from "react";
import {
  motion,
  useMotionValue,
  useAnimationFrame,
  AnimatePresence,
} from "framer-motion";
import ContactSection from "./ContactFrom";

const TESTIMONIALS = [
  {
    initials: "AP",
    color: "#E8A820",
    name: "AYUSH PATEL",
    role: "WeIntern Intern",
    stars: 5,
    text: '"Currently interning here and it’s been a great experience so far! The work culture is super chill and welcoming, and you actually get to work on real-world projects instead of just basic tasks. Everyone is really supportive whenever you get stuck. Glad I joined! 🙌"',
  },
  {
    initials: "AG",
    color: "#E8A820",
    name: "Aastha Gade",
    role: "WeIntern Student",
    stars: 4,
    text: '"Very nice and innovative place, best for career support and best guidance for future goals and skills development."',
  },
  {
    initials: "PD",
    color: "#9B59B6",
    name: "Prabina Das",
    role: "WeIntern Student",
    stars: 5,
    text: '"Best Platform for internships."',
  },
  {
    initials: "RK",
    color: "#E67E22",
    name: "Ria Kim",
    role: "WeIntern Student",
    stars: 4,
    text: '"Great workplaces don\'t just assign tasks, every task was an opportunity to learn something new. Thank you for an unforgettable learning journey!"',
  },
  {
    initials: "YP",
    color: "#18B45B",
    name: "Yashshree Pimpalkar",
    role: "WeIntern Student",
    stars: 5,
    text: '"My experience at WeIntern has been great so far. I\'m learning new things, gaining practical experience and getting valuable exposure to a professional work environment."',
  },
  {
    initials: "AP",
    color: "#F39C12",
    name: "Atasi Pradhan",
    role: "WeIntern Student",
    stars: 3,
    text: '"Best place to start career 🚀. Good learning environment and supportive team 😇."',
  },
  {
    initials: "PS",
    color: "#ffd23f",
    name: "Priyankarani Sahu",
    role: "WeIntern Student",
    stars: 5,
    text: '"Great experience with WeIntern! The mentors are supportive, the learning environment is excellent, and I gained valuable practical skills. Highly recommended for students and freshers looking for quality internship opportunities."',
  },
  {
    initials: "AD",
    color: "#E74C3C",
    name: "Aachal Deshmukh",
    role: "WeIntern Student",
    stars: 4,
    text: '"Excellent tech company! Professional team, modern office, and great client service."',
  },
  {
    initials: "AS",
    color: "#8E44AD",
    name: "Amiya Samal",
    role: "WeIntern Student",
    stars: 3,
    text: '"A great place to learn and improve your skills. Highly recommended."',
  },
];

const StarRating = ({ count }) => (
  <div className="flex shrink-0 items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <svg
        key={i}
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill={i < count ? "#f59e0b" : "#e2e8f0"}
        stroke="none"
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ))}
  </div>
);

const QuoteIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M9.983 3v7.391c0 5.704-3.731 9.57-8.983 10.609l-.995-2.151c2.432-.917 3.995-3.638 3.995-5.849h-4v-10h9.983zm14.017 0v7.391c0 5.704-3.748 9.571-9 10.609l-.996-2.151c2.433-.917 3.996-3.638 3.996-5.849h-3.983v-10h9.983z" />
  </svg>
);

const TestimonialCard = ({ t }) => (
  <div className="group relative w-[240px] shrink-0 overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_4px_18px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-indigo-200 hover:shadow-[0_10px_28px_rgba(15,23,42,0.1)] sm:w-[280px] sm:p-5">
    <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-indigo-500 via-blue-500 to-indigo-400 opacity-80" />

    <div className="relative z-10">
      <div className="flex items-start justify-between gap-2">
        <QuoteIcon className="mt-0.5 h-4 w-4 shrink-0 text-indigo-400/80" />
        <StarRating count={t.stars} />
      </div>

      <p
        className="mt-3 text-[12.5px] leading-[1.55] text-slate-600 sm:text-[13px]"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          minHeight: "58px",
        }}
      >
        {t.text}
      </p>

      <div className="mt-4 flex items-center gap-2.5 border-t border-slate-100 pt-3">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white shadow-sm"
          style={{ background: t.color }}
        >
          {t.initials}
        </div>

        <div className="min-w-0 flex-1">
          <div className="truncate text-[12.5px] font-semibold text-slate-900">
            {t.name}
          </div>
          <div className="truncate text-[10.5px] text-slate-500">
            {t.role}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const MarqueeRow = ({ items, speed = 30, direction = 1 }) => {
  const x = useMotionValue(0);
  const trackRef = useRef(null);
  const [oneSetWidth, setOneSetWidth] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const measure = () => {
      if (!trackRef.current) return;
      // 3 copies → divide by 3 to get exact width of one set
      const total = trackRef.current.scrollWidth;
      setOneSetWidth(total / 3);
    };

    const t = setTimeout(measure, 80);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
    };
  }, [items]);

  useAnimationFrame((_, delta) => {
    if (isPaused || !oneSetWidth) return;

    let next = x.get() - (direction * speed * delta) / 1000;

    if (direction > 0) {
      if (next <= -oneSetWidth) next += oneSetWidth;
    } else {
      if (next >= 0) next -= oneSetWidth;
    }

    x.set(next);
  });

  const triple = [...items, ...items, ...items];

  return (
    <div
      className="overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <motion.div
        ref={trackRef}
        style={{ x }}
        className="flex w-max gap-4 will-change-transform"
      >
        {triple.map((t, i) => (
          <TestimonialCard key={`${t.name}-${i}`} t={t} />
        ))}
      </motion.div>
    </div>
  );
};

const Testimonials = () => {
  const [showPopup, setShowPopup] = useState(false);

  const items = TESTIMONIALS.filter((t) => t.text && t.text.trim().length > 0);
  const mid = Math.ceil(items.length / 2);
  const rowA = items.slice(0, mid);
  const rowB = items.slice(mid);

  return (
    <>
      <section
        className="relative overflow-hidden bg-transparent pt-3 pb-14 sm:pt-6 sm:pb-24"
        id="testimonials"
      >
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-24 left-[8%] h-[300px] w-[300px] rounded-full bg-emerald-400/10 blur-[120px]" />
          <div className="absolute top-40 right-[6%] h-[280px] w-[280px] rounded-full bg-yellow-400/8 blur-[120px]" />
          <div className="absolute bottom-0 left-[35%] h-[260px] w-[260px] rounded-full bg-orange-400/7 blur-[120px]" />
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-transparent px-3.5 py-1.5 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(37,99,235,0.6)]" />
              <span
                className="text-[10px] font-bold uppercase tracking-[0.18em] text-blue-600 sm:text-[11px]"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Real Stories
              </span>
            </div>

            <h2
              className="text-[20px] font-semibold leading-[1.12] tracking-[-0.035em] text-slate-900 sm:text-3xl md:text-4xl lg:text-[30px]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              From Students{" "}
              <span className="relative inline-block text-blue-600">
                Who Made the Leap
                <span className="absolute -bottom-1 left-0 h-[3px] w-full rounded-full bg-blue-500/70" />
              </span>
            </h2>

            <p
              className="mx-auto mt-4 max-w-xl text-sm leading-6 text-slate-500 sm:mt-5 sm:text-base sm:leading-7"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Real experiences from students who turned learning into
              <span className="font-medium text-slate-700">
                {" "}
                skills, opportunities, and results.
              </span>
            </p>
          </motion.div>
        </div>

        <motion.div
          className="relative mt-9 space-y-4 sm:mt-14 sm:space-y-5"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
        >
          <MarqueeRow items={rowA} direction={1} speed={26} />
          <MarqueeRow items={rowB} direction={-1} speed={22} />
        </motion.div>

        {/* CTA Box */}
        <div className="relative mx-4 mt-16 w-auto max-w-5xl overflow-hidden rounded-2xl border border-slate-200/50 bg-transparent backdrop-blur-md px-4 py-5 shadow-lg sm:mx-auto sm:w-full sm:px-8 sm:py-8 lg:px-10">
          <div className="pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-emerald-400/[0.05] blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-40 w-40 rounded-full bg-emerald-500/[0.03] blur-3xl" />

          <div className="relative flex flex-col items-center justify-between gap-4 text-center md:flex-row md:gap-6 md:text-left">
            <motion.div
              className="max-w-xl"
              initial={{ opacity: 0, x: -15 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="mb-1.5 inline-flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-blue-600 sm:mb-2 sm:gap-2 sm:text-[10px]">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(37,99,235,0.5)]" />
                Start Building
              </div>

              <h3
                className="text-xl font-semibold leading-tight text-slate-900 sm:text-3xl"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Ready to turn internships into{" "}
                <span className="text-slate-900">real experience?</span>
              </h3>

              <p className="mt-1.5 max-w-lg text-[11px] leading-relaxed text-slate-600 sm:mt-2 sm:text-sm">
                Build real projects, learn from mentors, and create a portfolio
                that gets noticed.
              </p>
            </motion.div>

            <motion.div
              className="flex shrink-0 flex-wrap justify-center gap-2.5 sm:gap-3"
              initial={{ opacity: 0, x: 15 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <motion.button
                onClick={() => setShowPopup(true)}
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="group relative inline-flex items-center gap-1.5 overflow-hidden rounded-lg bg-blue-500 px-4 py-2.5 text-xs font-semibold text-white shadow-lg shadow-blue-500/25 sm:gap-2 sm:rounded-xl sm:px-5 sm:py-3 sm:text-sm hover:bg-blue-600"
              >
                <motion.span
                  className="absolute inset-y-0 -left-16 w-6 -skew-x-12 bg-white/20 blur-[1px]"
                  animate={{ x: [-60, 170] }}
                  transition={{
                    duration: 3.5,
                    repeat: Infinity,
                    repeatDelay: 2,
                    ease: "easeInOut",
                  }}
                />
                <span className="relative z-10">Join WeIntern</span>
                <span className="relative z-10 transition-transform duration-300 group-hover:translate-x-1">
                  →
                </span>
              </motion.button>

              <a
                href="#courses"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-medium text-slate-700 transition-all duration-300 hover:border-emerald-500 hover:bg-slate-100 hover:text-slate-900 sm:gap-2 sm:rounded-xl sm:px-5 sm:py-3 sm:text-sm"
              >
                Explore Courses
              </a>
            </motion.div>
          </div>
          <div className="absolute bottom-0 left-1/2 h-px w-1/2 -translate-x-1/2 bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent" />
        </div>
      </section>

      {/* POPUP */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowPopup(false)}
          >
            <motion.div
              className="relative grid w-full max-w-3xl grid-cols-1 overflow-hidden rounded-[28px] md:grid-cols-[0.9fr_1.1fr]"
              style={{
                background: "rgba(255, 255, 255, 0.48)",
                backdropFilter: "blur(32px)",
                WebkitBackdropFilter: "blur(32px)",
                border: "1px solid rgba(255, 255, 255, 0.45)",
                boxShadow:
                  "0 25px 60px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.6)",
              }}
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ type: "spring", stiffness: 300, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowPopup(false)}
                aria-label="Close"
                className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-black/10 bg-white/50 text-black backdrop-blur-md transition hover:bg-white/80"
              >
                ×
              </button>

              {/* LEFT PANEL */}
              <div className="relative hidden flex-col justify-between overflow-hidden p-8 text-black md:flex">
                <div className="pointer-events-none absolute inset-0 opacity-20">
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage:
                        "linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)",
                      backgroundSize: "28px 28px",
                    }}
                  />
                </div>

                <div className="relative">
                  <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/40 px-3.5 py-1.5 text-[10px] font-medium tracking-wide text-black backdrop-blur-md">
                    Let's get you started
                  </div>

                  <h4
                    className="mt-5 text-2xl font-semibold leading-snug text-black"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    Join the ecosystem building real careers.
                  </h4>

                  <p className="mt-3 text-sm leading-relaxed text-black/60">
                    Tell us a bit about yourself and a mentor will reach out to
                    guide you through the next steps.
                  </p>
                </div>

                <div className="mt-8 space-y-3">
                  <div
                    className="group flex items-center gap-4 rounded-2xl px-4 py-3.5 transition-all duration-300 hover:-translate-y-0.5"
                    style={{
                      background: "rgba(255, 255, 255, 0.58)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(255, 255, 255, 0.55)",
                      boxShadow:
                        "0 4px 16px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.6)",
                    }}
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/60 text-black border border-black/8">
                      ✉
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-black/50">
                        Email
                      </span>
                      <p className="mt-0.5 truncate text-[13px] font-semibold text-black">
                        contact@we-intern.in
                      </p>
                    </div>
                    <span className="text-black/35 group-hover:text-black/70 transition">
                      ↗
                    </span>
                  </div>

                  <div
                    className="group flex items-center gap-4 rounded-2xl px-4 py-3.5 transition-all duration-300 hover:-translate-y-0.5"
                    style={{
                      background: "rgba(255, 255, 255, 0.58)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(255, 255, 255, 0.55)",
                      boxShadow:
                        "0 4px 16px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.6)",
                    }}
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/60 text-black border border-black/8">
                      ☎
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-black/50">
                        Phone
                      </span>
                      <p className="mt-0.5 truncate text-[13px] font-semibold text-black">
                        +91 7414974582
                      </p>
                    </div>
                    <span className="text-black/35 group-hover:text-black/70 transition">
                      ↗
                    </span>
                  </div>

                  <div
                    className="group flex items-center gap-4 rounded-2xl px-4 py-3.5 transition-all duration-300 hover:-translate-y-0.5"
                    style={{
                      background: "rgba(255, 255, 255, 0.58)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(255, 255, 255, 0.55)",
                      boxShadow:
                        "0 4px 16px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.6)",
                    }}
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/60 text-black border border-black/8">
                      📍
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-black/50">
                        Location
                      </span>
                      <p className="mt-0.5 truncate text-[13px] font-semibold text-black">
                        Pune, Maharashtra, India
                      </p>
                    </div>
                    <span className="text-black/35 group-hover:text-black/70 transition">
                      ↗
                    </span>
                  </div>
                </div>
              </div>

              {/* RIGHT SIDE */}
              <div className="p-2 sm:p-3">
                <ContactSection />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Testimonials;